import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, ArrowRight, LayoutGrid } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SERVICES, FEATURED_SERVICES, scrollToId } from "../config/business";

const ServiceCard = ({ service, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={(index % 3) * 0.06} className="h-full">
      <article className="group flex h-full flex-col border border-white/10 bg-[#141519] p-6 transition-colors duration-300 hover:border-[#00AEEF]/60">
        <div className="flex items-start justify-between">
          <service.icon className="h-8 w-8 text-[#00AEEF]" strokeWidth={1.6} />
          <span className="font-display text-sm font-bold text-white/20">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        {service.badge && (
          <span className="mt-4 inline-flex w-fit items-center border border-[#00AEEF]/50 bg-[#00AEEF]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#00AEEF]">
            {service.badge}
          </span>
        )}
        <h3 className="font-display mt-4 text-lg font-bold uppercase leading-snug tracking-wide text-[#f4f4f2]">
          {service.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-[#a6a9b1]">
          {service.blurb}
        </p>
        <AnimatePresence initial={false}>
          {open && (
            <motion.p
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden text-sm leading-relaxed text-[#c9ccd2]"
            >
              <span className="mt-3 block border-l-2 border-[#00AEEF] pl-3">
                {service.detail}
              </span>
            </motion.p>
          )}
        </AnimatePresence>
        <button
          data-testid={`service-learn-more-${service.slug}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="font-display mt-5 flex items-center gap-2 self-start text-xs font-bold uppercase tracking-[0.2em] text-[#00AEEF] transition-colors hover:text-white"
        >
          {open ? "Show Less" : "Learn More"}
          <Plus
            className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-45" : ""}`}
          />
        </button>
      </article>
    </Reveal>
  );
};

export const Services = () => {
  const featured = FEATURED_SERVICES.map((slug) =>
    SERVICES.find((s) => s.slug === slug)
  );
  return (
    <section id="services" className="spotlight relative bg-[#0b0c0e] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <SectionHeading
            kicker="What We Do"
            title="Full-Service Mechanical Repair"
          />
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#a6a9b1]">
            From routine maintenance and diagnostics to major mechanical repairs
            and engine work, Auto Trust Centre provides complete automotive
            service under one roof — for all makes and models, with deep
            experience in domestic and Asian vehicles.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>

        <Reveal className="mt-10 text-center">
          <Link
            data-testid="services-view-all-button"
            to="/services"
            className="btn-ghost"
          >
            <LayoutGrid className="h-5 w-5 text-[#00AEEF]" />
            View All Services
          </Link>
        </Reveal>

        <Reveal className="mt-14">
          <div className="flex flex-col items-start justify-between gap-6 border border-white/10 bg-[#141519] p-8 lg:flex-row lg:items-center lg:p-12">
            <div>
              <h3 className="font-display text-2xl font-bold uppercase text-[#f4f4f2] sm:text-3xl">
                Not Sure What Your Vehicle Needs?
              </h3>
              <p className="mt-2 max-w-xl text-[#a6a9b1]">
                Tell us what's happening with your vehicle and we'll help
                determine the next step.
              </p>
            </div>
            <button
              data-testid="services-contact-button"
              onClick={() => scrollToId("contact")}
              className="btn-primary shrink-0"
            >
              Contact Auto Trust Centre
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
